import React, { useMemo } from "react";
import { Button, Card, Col, Collapse, Row, Select, Space, Statistic, Tag, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { CustomTable } from "../../series/StyledComponent";
import { validateAllConstraints } from "../utils/constraintValidation";
import { PlanWarning } from "../utils/batchJobWarnings";
import { getColorForAdvertiser, getTextColorForBackground } from "../utils/colorUtils";
import {
    getAdSelectionCounts,
    getFilledLengthForSlot,
    getSlotDollarValue,
    calculateTotalDollarValue,
    calculateTotalAvailableAdsTime,
    calculateTotalUnfilledTime,
    getDayOfWeekUsageCount,
    normalizeDay,
    getStartingUnits,
    calculateUtilization,
} from "../utils/slotTableUtils";
import { FixedOverfullProgressBar } from "./FixedOverfullProgressBar";
import { RejectionReasonsModal } from "../RejectionReasonsModal";
import {
    RejectionReason,
    AutopodderResultRow,
    ScheduleDataItem,
} from "../types";

export interface SlotTableCoreProps {
    poddedData: AutopodderResultRow[];
    scheduleData: { [key: string]: ScheduleDataItem };
    slotSelectionDict: { [key: string]: string[] };
    setSlotSelectionDict: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
    loading: boolean;
    getOriginalAdSelectionCounts: () => { [scheduleName: string]: number };
    mode?: 'review' | 'preseed';
    /** Warnings from the autopodder engine (shown alongside constraint violations) */
    warnings?: PlanWarning[];
    /** Currently selected strategy name (used for Fill Order column visibility) */
    selectedStrategy?: string | null;
    /** Position dict for pre-seed mode: { slotId: { adName: 'start' | 'end' } } */
    slotPositionDict?: { [slotId: string]: { [adName: string]: 'start' | 'end' } };
    setSlotPositionDict?: React.Dispatch<React.SetStateAction<{ [slotId: string]: { [adName: string]: 'start' | 'end' } }>>;
}

const SlotTableCore: React.FC<SlotTableCoreProps> = ({
    poddedData,
    scheduleData,
    slotSelectionDict,
    setSlotSelectionDict,
    loading,
    getOriginalAdSelectionCounts,
    mode = 'review',
    warnings = [],
    selectedStrategy = null,
    slotPositionDict,
    setSlotPositionDict,
}) => {
    const [rejectionModalVisible, setRejectionModalVisible] = React.useState<boolean>(false);
    const [selectedSlotForRejections, setSelectedSlotForRejections] = React.useState<AutopodderResultRow | null>(null);

    // Show spinner until data has actually arrived at least once.
    // This avoids flashes of empty table while concurrent fetches settle.
    const [initialDataReceived, setInitialDataReceived] = React.useState(false);
    React.useEffect(() => {
        if (poddedData.length > 0) setInitialDataReceived(true);
    }, [poddedData]);
    const effectiveLoading = !initialDataReceived || loading;

    const getAdTitle = (adName: string) => {
        const titleParts = [];
        if (scheduleData[adName]) {
            if (scheduleData[adName].UNITLENGTH) {
                titleParts.push(scheduleData[adName].UNITLENGTH.toString());
            }
            if (scheduleData[adName].DEALBRAND) {
                titleParts.push(scheduleData[adName].DEALBRAND);
            }
            if (scheduleData[adName].ADVERTISER_NAME) {
                titleParts.push(scheduleData[adName].ADVERTISER_NAME);
            }
            titleParts.push(adName);
            if (scheduleData[adName].RATE_STR) {
                titleParts.push(`($${scheduleData[adName].RATE_STR})`);
            }
            if (scheduleData[adName].ISCI_CODE) {
                titleParts.push(`[ISCI: ${scheduleData[adName].ISCI_CODE}]`);
            }
        }
        return titleParts.join(" - ");
    };

    const handleAdSelect = (value: string, record: AutopodderResultRow) => {
        const scheduleItem = scheduleData[value];
        if (!scheduleItem) return;

        const currentSelectionCounts = getAdSelectionCounts(poddedData, slotSelectionDict);
        const originalSelectionCounts = getOriginalAdSelectionCounts();
        const currentCount = currentSelectionCounts[value] || 0;
        const originalCount = originalSelectionCounts[value] || 0;
        const realTimeRemaining = Math.max(0, scheduleItem.UNITS_REMAINING + originalCount - currentCount);

        if (realTimeRemaining === 0) {
            return;
        }

        // Check max per day constraint
        const maxPerDay = scheduleItem.MAX_PER_DAY;
        if (maxPerDay !== undefined && maxPerDay !== null && maxPerDay > 0) {
            const slotDayOfWeek = record.DAY_OF_WEEK || '';
            const currentDayUsage = getDayOfWeekUsageCount(value, slotDayOfWeek, poddedData, slotSelectionDict);
            if (currentDayUsage >= maxPerDay) {
                return;
            }
        }

        setSlotSelectionDict((prev) => {
            const currentAds = prev[record.ID] || [];
            return {
                ...prev,
                [record.ID]: [...currentAds, value]
            };
        });
    };

    const getAdOptions = (SLOT_ID: string) => {
        const adSelectionCounts = getAdSelectionCounts(poddedData, slotSelectionDict);
        const slotRecord = poddedData.find(slot => slot.ID === SLOT_ID);
        const currentSlotSelections = slotRecord ? (slotSelectionDict[slotRecord.ID] || []) : [];
        const slotDayOfWeek = slotRecord?.DAY_OF_WEEK || '';
        const normalizedSlotDay = normalizeDay(slotDayOfWeek);

        return Object.keys(scheduleData)
            .filter((scheduleName) => {
                const scheduleItem = scheduleData[scheduleName];
                if (!scheduleItem) return false;

                if (currentSlotSelections.includes(scheduleName)) {
                    return true;
                }

                const adLength = scheduleItem.UNITLENGTH;
                const slotMaxSize = slotRecord?.SIZE_MAX;
                if (adLength !== undefined && adLength !== null && slotMaxSize !== undefined && slotMaxSize !== null) {
                    if (adLength > slotMaxSize) {
                        return false;
                    }
                }

                const daysAllowedStr = scheduleItem.DAYS_ALLOWED || '';
                if (daysAllowedStr) {
                    const allowedDays = daysAllowedStr.split(',').map(d => normalizeDay(d.trim()));
                    if (!allowedDays.includes(normalizedSlotDay)) {
                        return false;
                    }
                }

                const maxPerDay = scheduleItem.MAX_PER_DAY;
                if (maxPerDay !== undefined && maxPerDay !== null && maxPerDay > 0) {
                    const currentDayUsage = getDayOfWeekUsageCount(scheduleName, slotDayOfWeek, poddedData, slotSelectionDict);
                    if (currentDayUsage >= maxPerDay) {
                        return false;
                    }
                }

                return true;
            })
            .sort((a, b) => {
                const aLength = scheduleData[a]?.UNITLENGTH ?? 0;
                const bLength = scheduleData[b]?.UNITLENGTH ?? 0;
                return bLength - aLength;
            })
            .map((scheduleName) => {
                const scheduleItem = scheduleData[scheduleName];
                const backgroundColor = scheduleItem?.ADVERTISER_ID
                    ? getColorForAdvertiser(scheduleItem.ADVERTISER_ID)
                    : '#f0f0f0';
                const textColor = getTextColorForBackground(backgroundColor);

                const originalSelectionCounts = getOriginalAdSelectionCounts();
                const currentCount = adSelectionCounts[scheduleName] || 0;
                const originalCount = originalSelectionCounts[scheduleName] || 0;
                const realTimeRemaining = Math.max(0, scheduleItem.UNITS_REMAINING + originalCount - currentCount);

                const isSelectedInCurrentSlot = currentSlotSelections.includes(scheduleName);

                const maxPerDay = scheduleItem.MAX_PER_DAY;
                let dayUsageCount = 0;
                let maxPerDayExceeded = false;
                if (maxPerDay !== undefined && maxPerDay !== null && maxPerDay > 0) {
                    dayUsageCount = getDayOfWeekUsageCount(scheduleName, slotDayOfWeek, poddedData, slotSelectionDict);
                    maxPerDayExceeded = dayUsageCount >= maxPerDay && !isSelectedInCurrentSlot;
                }

                const shouldDisable = (realTimeRemaining === 0 || maxPerDayExceeded) && !isSelectedInCurrentSlot;

                const adTitle = getAdTitle(scheduleName);
                const label = (
                    <Space>
                        <span>{adTitle}</span>
                        <span style={{
                            fontSize: '11px',
                            color: (realTimeRemaining === 0 || maxPerDayExceeded) ? '#ff4d4f' : '#666666',
                            fontWeight: (realTimeRemaining === 0 || maxPerDayExceeded) ? 'bold' : 'normal',
                            marginLeft: '8px'
                        }}>
                            ({realTimeRemaining} unit{realTimeRemaining !== 1 ? 's' : ''} remaining
                            {maxPerDay !== undefined && maxPerDay !== null && maxPerDay > 0 && (
                                <>, {dayUsageCount}/{maxPerDay} per day</>
                            )})
                        </span>
                    </Space>
                );

                return {
                    key: `option-${SLOT_ID}-${scheduleName}-${currentCount}-${realTimeRemaining}`,
                    value: scheduleName,
                    label: label,
                    searchText: adTitle,
                    style: { backgroundColor, color: textColor },
                    disabled: shouldDisable
                };
            });
    };

    // Parse rejection reasons from JSON string
    const getRejectionReasons = (record: AutopodderResultRow): RejectionReason[] => {
        if (!record.REJECTION_REASONS) {
            return [];
        }
        try {
            return JSON.parse(record.REJECTION_REASONS) as RejectionReason[];
        } catch (error) {
            console.error('Error parsing rejection reasons:', error);
            return [];
        }
    };

    // Get current units available for an ad from scheduleData
    const getCurrentUnitsAvailable = (adName: string): number => {
        const scheduleItem = scheduleData[adName];
        if (!scheduleItem) return 0;

        const currentSelectionCounts = getAdSelectionCounts(poddedData, slotSelectionDict);
        const originalSelectionCounts = getOriginalAdSelectionCounts();
        const currentCount = currentSelectionCounts[adName] || 0;
        const originalCount = originalSelectionCounts[adName] || 0;
        return Math.max(0, scheduleItem.UNITS_REMAINING + originalCount - currentCount);
    };

    // Real-time constraint violation warnings
    const constraintWarnings = useMemo<PlanWarning[]>(() => {
        if (poddedData.length === 0 || Object.keys(scheduleData).length === 0) return [];
        const origCounts = getOriginalAdSelectionCounts();
        return validateAllConstraints({
            poddedData,
            slotSelectionDict,
            scheduleData,
            originalSelectionCounts: origCounts,
        });
    }, [poddedData, slotSelectionDict, scheduleData]);

    const podResultColumns = [
        {
            title: 'Slot ID',
            dataIndex: 'SLOT_ID',
            key: 'SLOT_ID',
            sorter: (a: AutopodderResultRow, b: AutopodderResultRow) => {
                const aSlotId = a.SLOT_ID || '';
                const bSlotId = b.SLOT_ID || '';
                return aSlotId.localeCompare(bSlotId);
            },
            render: (_text: string, record: AutopodderResultRow) => {
                return (
                    <div>
                        <div>{record.SLOT_ID}</div>
                        {record.DATE && (
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                                Date: <span style={{ fontWeight: 'bold' }}>{record.DATE}</span>
                            </div>
                        )}
                        {record.SEGMENT_ID && (
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                                SEGMENT_ID: {record.SEGMENT_ID}
                            </div>
                        )}
                    </div>
                );
            },
        },
        // Show Fill Order only in review mode when a strategy is selected
        ...(mode === 'review' && selectedStrategy ? [{
            title: 'Fill Order',
            dataIndex: 'FILLORDER',
            key: 'FILLORDER',
            sorter: (a: AutopodderResultRow, b: AutopodderResultRow) => {
                const aOrder = a.FILLORDER || 0;
                const bOrder = b.FILLORDER || 0;
                return aOrder - bOrder;
            },
            defaultSortOrder: 'ascend' as const,
        }] : []),
        {
            title: 'Day of Week',
            dataIndex: 'DAY_OF_WEEK',
            key: 'DAY_OF_WEEK',
        },
        {
            title: 'Fill Status',
            dataIndex: 'SELECTED_AD_COMBO_TOTAL_ADS_LENGTH',
            key: 'SELECTED_AD_COMBO_TOTAL_ADS_LENGTH',
            render: (_text: string, record: AutopodderResultRow) => {
                const filledLength = getFilledLengthForSlot(record.ID, slotSelectionDict, scheduleData);
                const maxLength = record.SIZE_MAX;
                return (
                    <FixedOverfullProgressBar currentAmount={filledLength} totalAmount={maxLength} />
                );
            }
        },
        {
            title: 'PODDING',
            dataIndex: 'SELECTED_AD_COMBO_ADS_STR',
            key: 'SELECTED_AD_COMBO_ADS_STR',
            render: (_text: string, record: AutopodderResultRow) => {
                const adNames = slotSelectionDict[record.ID] || [];
                const currentSelectionsKey = adNames.join(',');
                const allSelectionsKey = Object.values(slotSelectionDict).flat().join(',');
                return (
                    <>
                        <Select
                            key={`select-${record.ID}-${currentSelectionsKey}-${allSelectionsKey}`}
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder="Please select"
                            value={adNames}
                            optionFilterProp="searchText"
                            filterOption={(input, option) => {
                                const searchText = (option as { searchText?: string })?.searchText || '';
                                return searchText.toLowerCase().includes(input.toLowerCase());
                            }}
                            onSelect={(value) => handleAdSelect(value as string, record)}
                            onDeselect={(value) => {
                                const scheduleItem = scheduleData[value as string];
                                if (!scheduleItem) return;

                                const currentSelectionCounts = getAdSelectionCounts(poddedData, slotSelectionDict);
                                const originalSelectionCounts = getOriginalAdSelectionCounts();
                                const currentCount = currentSelectionCounts[value as string] || 0;
                                const originalCount = originalSelectionCounts[value as string] || 0;
                                const realTimeRemaining = Math.max(0, scheduleItem.UNITS_REMAINING + originalCount - currentCount);

                                if (realTimeRemaining === 0) {
                                    setSlotSelectionDict((prev) => {
                                        const currentAds = prev[record.ID] || [];
                                        const index = currentAds.indexOf(value as string);
                                        if (index !== -1) {
                                            const newAds = [...currentAds];
                                            newAds.splice(index, 1);
                                            return {
                                                ...prev,
                                                [record.ID]: newAds
                                            };
                                        }
                                        return prev;
                                    });
                                } else {
                                    handleAdSelect(value as string, record);
                                }
                            }}
                            onChange={() => {
                                // onChange is called when tags are closed via X button
                                // Let tagRender's onClose handle it instead
                            }}
                            options={getAdOptions(record.ID)}
                            tagRender={(props) => {
                                const { value, closable } = props;
                                const tagScheduleItem = scheduleData[value as string];
                                const backgroundColor = tagScheduleItem?.ADVERTISER_ID
                                    ? getColorForAdvertiser(tagScheduleItem.ADVERTISER_ID)
                                    : '#f0f0f0';
                                const textColor = getTextColorForBackground(backgroundColor);

                                const currentSelectionCounts = getAdSelectionCounts(poddedData, slotSelectionDict);
                                const originalSelectionCounts = getOriginalAdSelectionCounts();
                                const currentCount = currentSelectionCounts[value as string] || 0;
                                const originalCount = originalSelectionCounts[value as string] || 0;
                                const realTimeRemaining = tagScheduleItem ? Math.max(0, tagScheduleItem.UNITS_REMAINING + originalCount - currentCount) : 0;

                                const tooltipContent = tagScheduleItem
                                    ? `Schedule: ${value}
Advertiser: ${tagScheduleItem.ADVERTISER_NAME || 'N/A'}
Current Units Remaining: ${tagScheduleItem.UNITS_REMAINING} / ${getStartingUnits(tagScheduleItem)}
Projected Remaining: ${realTimeRemaining} (after ${currentCount} selection${currentCount !== 1 ? 's' : ''})
Utilization: ${calculateUtilization(getStartingUnits(tagScheduleItem), tagScheduleItem.UNITS_REMAINING)}%
Allowed Days: ${tagScheduleItem.DAYS_ALLOWED || 'N/A'}
Max Per Day: ${tagScheduleItem.MAX_PER_DAY || 'N/A'}
Advertiser Max Per Day: ${tagScheduleItem.ADV_MAX_PER_DAY_CALC || 'N/A'}
Min Separation: ${tagScheduleItem.MIN_SEP || 'N/A'}
Advertiser Separation: ${tagScheduleItem.ADV_SEP || 'N/A'}
Normalised Rate: ${tagScheduleItem.RATE_INT_NORMALISED || 'N/A'}
Rate: ${tagScheduleItem.RATE_STR || 'N/A'}
ISCI: ${tagScheduleItem.ISCI_CODE || 'MISSING — no traffic on file'}`
                                    : 'No inventory data available';

                                const adTitle = getAdTitle(value as string);

                                const handleTagClose = (e: React.MouseEvent<HTMLElement>) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSlotSelectionDict((prev) => {
                                        const currentAds = prev[record.ID] || [];
                                        const index = currentAds.indexOf(value as string);
                                        if (index !== -1) {
                                            const newAds = [...currentAds];
                                            newAds.splice(index, 1);
                                            return {
                                                ...prev,
                                                [record.ID]: newAds
                                            };
                                        }
                                        return prev;
                                    });
                                };

                                const missingTraffic = !!tagScheduleItem && !tagScheduleItem.ISCI_CODE;

                                return (
                                    <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tooltipContent}</pre>}>
                                        <Tag
                                            closable={true}
                                            onClose={handleTagClose}
                                            style={{
                                                marginRight: 3,
                                                marginBottom: 3,
                                                border: missingTraffic ? '2px solid #ff4d4f' : `1px solid ${backgroundColor}`,
                                                backgroundColor: backgroundColor,
                                                color: textColor,
                                                fontWeight: 500,
                                                boxShadow: missingTraffic ? '0 0 0 1px #ff4d4f inset' : undefined
                                            }}
                                        >
                                            {missingTraffic && (
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        marginRight: 6,
                                                        padding: '0 4px',
                                                        borderRadius: 3,
                                                        backgroundColor: '#ff4d4f',
                                                        color: '#fff',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        letterSpacing: 0.3
                                                    }}
                                                >
                                                    ⚠ NO TRAFFIC
                                                </span>
                                            )}
                                            {adTitle} ({realTimeRemaining} remaining)
                                            {mode === 'preseed' && slotPositionDict && setSlotPositionDict && (
                                                <span
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const adName = value as string;
                                                        setSlotPositionDict(prev => {
                                                            const current = prev[record.ID]?.[adName] || 'start';
                                                            return {
                                                                ...prev,
                                                                [record.ID]: {
                                                                    ...prev[record.ID],
                                                                    [adName]: current === 'start' ? 'end' : 'start',
                                                                },
                                                            };
                                                        });
                                                    }}
                                                    style={{
                                                        marginLeft: 6,
                                                        cursor: 'pointer',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        padding: '1px 4px',
                                                        borderRadius: '3px',
                                                        backgroundColor: (slotPositionDict[record.ID]?.[value as string] || 'start') === 'start'
                                                            ? 'rgba(0,0,0,0.15)' : 'rgba(255,165,0,0.3)',
                                                        color: textColor,
                                                    }}
                                                    title="Click to toggle: ad plays at start or end of break"
                                                >
                                                    {(slotPositionDict[record.ID]?.[value as string] || 'start') === 'start' ? '▶ START' : '◀ END'}
                                                </span>
                                            )}
                                        </Tag>
                                    </Tooltip>
                                );
                            }}
                        />
                        <div style={{
                            marginTop: 8,
                            fontSize: '13px',
                            fontWeight: 600,
                            color: getSlotDollarValue(record.ID, slotSelectionDict, scheduleData) > 0 ? '#52c41a' : '#999'
                        }}>
                            Slot Value: ${getSlotDollarValue(record.ID, slotSelectionDict, scheduleData).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </>
                );
            }
        },
        // Show Rejection Reasons only in review mode
        ...(mode === 'review' ? [{
            title: 'Rejection Reasons',
            key: 'REJECTION_REASONS',
            width: 120,
            render: (_text: string, record: AutopodderResultRow) => {
                const rejectionReasons = getRejectionReasons(record);
                if (rejectionReasons.length === 0) {
                    return <span style={{ color: '#999' }}>-</span>;
                }
                return (
                    <Button
                        type="link"
                        icon={<SearchOutlined />}
                        onClick={() => {
                            setSelectedSlotForRejections(record);
                            setRejectionModalVisible(true);
                        }}
                        style={{ padding: 0 }}
                    >
                        View ({rejectionReasons.length})
                    </Button>
                );
            },
        }] : []),
    ];

    const totalDollarValue = calculateTotalDollarValue(poddedData, slotSelectionDict, scheduleData);
    const totalAdsTime = calculateTotalAvailableAdsTime(poddedData, slotSelectionDict, scheduleData);
    const totalUnfilledTime = calculateTotalUnfilledTime(poddedData, slotSelectionDict, scheduleData);

    return (
        <>
            {(constraintWarnings.length > 0 || warnings.length > 0) && (
                <Collapse
                    defaultActiveKey={constraintWarnings.length > 0 ? ['constraint-violations'] : []}
                    items={[
                        ...(constraintWarnings.length > 0 ? [{
                            key: 'constraint-violations',
                            label: (
                                <span style={{ fontWeight: 600, fontSize: '16px', color: '#ff4d4f' }}>
                                    Constraint Violations ({constraintWarnings.length})
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" style={{ width: "100%" }} size="small">
                                    {constraintWarnings.map((warning, index) => (
                                        <div key={index} style={{ fontSize: '14px' }}>
                                            <Tag color="error" style={{ marginRight: 8 }}>
                                                {warning.type}
                                            </Tag>
                                            {warning.message}
                                        </div>
                                    ))}
                                </Space>
                            ),
                            style: { borderColor: '#ff4d4f', backgroundColor: '#fff2f0' }
                        }] : []),
                        ...(warnings.length > 0 ? [{
                            key: 'autopodder-warnings',
                            label: (
                                <span style={{ fontWeight: 600, fontSize: '16px', color: '#faad14' }}>
                                    Autopodder Warnings ({warnings.length})
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" style={{ width: "100%" }} size="small">
                                    {warnings.map((warning, index) => (
                                        <div key={index} style={{ fontSize: '14px' }}>
                                            <Tag color="warning" style={{ marginRight: 8 }}>
                                                {warning.type}
                                            </Tag>
                                            {warning.message}
                                        </div>
                                    ))}
                                </Space>
                            ),
                            style: { borderColor: '#faad14', backgroundColor: '#fffbe6' }
                        }] : []),
                    ]}
                    style={{ marginBottom: 16 }}
                />
            )}
            <Space direction="vertical" style={{ width: "100%" }} size="large">
                <Card>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Statistic
                                title="Total Dollar Value"
                                value={totalDollarValue}
                                prefix="$"
                                valueStyle={{ fontSize: '20px', fontWeight: 'bold' }}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Total Available Ads Time"
                                value={totalAdsTime}
                                suffix="s"
                                valueStyle={{ fontSize: '20px', fontWeight: 'bold' }}
                            />
                        </Col>
                        <Col span={8}>
                            {(() => {
                                if (totalUnfilledTime < 0) {
                                    return (
                                        <Statistic
                                            title="Total Unfilled Time"
                                            value={Math.abs(totalUnfilledTime)}
                                            prefix="-"
                                            suffix="s (overfilled)"
                                            valueStyle={{ fontSize: '20px', fontWeight: 'bold', color: '#ff7a45' }}
                                        />
                                    );
                                } else if (totalUnfilledTime > 0) {
                                    return (
                                        <Statistic
                                            title="Total Unfilled Time"
                                            value={totalUnfilledTime}
                                            suffix="s"
                                            valueStyle={{ fontSize: '20px', fontWeight: 'bold', color: '#ff4d4f' }}
                                        />
                                    );
                                } else {
                                    return (
                                        <Statistic
                                            title="Total Unfilled Time"
                                            value={0}
                                            suffix="s"
                                            valueStyle={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}
                                        />
                                    );
                                }
                            })()}
                        </Col>
                    </Row>
                </Card>
                <Card
                    title={
                        <Space>
                            <span>{mode === 'preseed' ? 'Pre-Seed Slots' : 'Podded Results'}</span>
                            {mode === 'review' && selectedStrategy && (
                                <Tag color="blue">{selectedStrategy} Strategy</Tag>
                            )}
                            <span style={{ fontSize: '14px', fontWeight: 'normal', color: 'rgba(255, 255, 255, 0.65)' }}>
                                ({poddedData.length} slots)
                            </span>
                        </Space>
                    }
                >
                    <CustomTable
                        columns={podResultColumns as never}
                        dataSource={poddedData}
                        loading={effectiveLoading}
                        locale={{ emptyText: ' ' }}
                        pagination={false}
                        bordered={true}
                        rowKey="ID"
                    />
                </Card>
            </Space>
            {mode === 'review' && (
                <RejectionReasonsModal
                    open={rejectionModalVisible}
                    onClose={() => {
                        setRejectionModalVisible(false);
                        setSelectedSlotForRejections(null);
                    }}
                    selectedSlot={selectedSlotForRejections}
                    scheduleData={scheduleData}
                    getCurrentUnitsAvailable={getCurrentUnitsAvailable}
                />
            )}
        </>
    );
};

export default SlotTableCore;
